import React, { useState, useEffect } from 'react';
import { useAuth, useFirebase } from '../context/AuthContext';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Plus, Trash2, Edit } from 'lucide-react';
import InputField from '../components/InputField';

const InstitutionalData = ({ onLoginRequest }) => {
    const { user } = useAuth();
    const { db } = useFirebase();
    const [data, setData] = useState({
        ci100Tolerance: '1.2',
        dataType: 'points',
        useR50: true,
        useD2cm: true,
        customTable: [{ ptv: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' }]
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);

    useEffect(() => {
        const defaultData = {
            ci100Tolerance: '1.2', dataType: 'points', useR50: true, useD2cm: true,
            customTable: [{ ptv: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' }]
        };

        if (!db || !user?.uid || user.isAnonymous) {
            setData(defaultData);
            return;
        }

        const docRef = doc(db, `users/${user.uid}/institutionalData`, 'customValues');
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const fetchedData = docSnap.data();
                if (!fetchedData.customTable || fetchedData.customTable.length === 0) {
                    fetchedData.customTable = defaultData.customTable;
                }
                setData(fetchedData);
            } else {
                setData(defaultData);
            }
        });
        return () => unsubscribe();
    }, [db, user]);
    
    const handleSave = async () => {
        if (!user || user.isAnonymous) {
            onLoginRequest();
            return;
        }
        setIsSaving(true);
        setSaveStatus(null);
        try {
            const docRef = doc(db, `users/${user.uid}/institutionalData`, 'customValues');
            await setDoc(docRef, data);
            setSaveStatus('success');
        } catch (error) {
            console.error('Error saving institutional data:', error);
            setSaveStatus('error');
        }
        setIsSaving(false);
        setTimeout(() => setSaveStatus(null), 3000);
    };

    const handleTableChange = (index, field, value) => {
        setData(prevData => {
            const newTable = JSON.parse(JSON.stringify(prevData.customTable));
            newTable[index][field] = value;
            return { ...prevData, customTable: newTable };
        });
    };
    
    const handleAddRow = () => {
        setData(prevData => {
            const newRow = prevData.dataType === 'points'
              ? { ptv: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' }
              : { ptvMin: '', ptvMax: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' };
            return { ...prevData, customTable: [...prevData.customTable, newRow] };
        });
    };

    const handleRemoveRow = (index) => {
        setData(prevData => {
            const newTable = prevData.customTable.filter((_, i) => i !== index);
            if (newTable.length === 0) {
                const newRow = prevData.dataType === 'points'
                  ? { ptv: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' }
                  : { ptvMin: '', ptvMax: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' };
                newTable.push(newRow);
            }
            return { ...prevData, customTable: newTable };
        });
    };

    const onToleranceChange = (value) => {
        setData(prevData => ({ ...prevData, ci100Tolerance: value }));
    };
    
    const onConfigChange = (field, value) => {
        setData(prevData => {
            const newData = { ...prevData, [field]: value };
            if (field === 'dataType') {
                const newRow = value === 'points'
                  ? { ptv: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' }
                  : { ptvMin: '', ptvMax: '', r50p: '', r50v: '', d2cmp: '', d2cmv: '' };
                newData.customTable = [newRow];
            }
            return newData;
        });
    };

    return (
      <div className="page-content p-4 md:p-6">
        <h2 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-cyan-400 mb-4 text-center">My Institutional Data</h2>
        <p className="text-gray-600 dark:text-gray-400 text-base md:text-lg text-center mb-8">
            Enter and manage your own institutional benchmarks. The calculator will use this data when the "Use My Institutional Data" checkbox is enabled on the calculator pages.
            <br/>Your user ID: <span className="text-blue-600 dark:text-blue-400 font-mono break-all">{user?.uid || 'Anonymous'}</span>
        </p>
        
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-300 dark:border-gray-700 mb-8">
            <h3 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-4">Data Configuration</h3>
            <div className="space-y-4">
                <div>
                    <label className="block text-md font-medium text-gray-700 dark:text-gray-300 mb-2">CI100 Tolerance</label>
                    <InputField label="" value={data.ci100Tolerance} onChange={e => onToleranceChange(e.target.value)} step="0.01" min="0" />
                </div>
                <div>
                    <label className="block text-md font-medium text-gray-700 dark:text-gray-300 mb-2">PTV Data Entry Format</label>
                    <div className="flex space-x-2 rounded-lg bg-gray-200 dark:bg-gray-900 p-1">
                        <button onClick={() => onConfigChange('dataType', 'points')} className={`w-full py-2 rounded-lg font-semibold transition-colors ${data.dataType === 'points' ? 'bg-blue-600 text-white' : 'hover:bg-gray-300 dark:hover:bg-gray-700'}`}>
                            PTV Points (Interpolation)
                        </button>
                        <button onClick={() => onConfigChange('dataType', 'ranges')} className={`w-full py-2 rounded-lg font-semibold transition-colors ${data.dataType === 'ranges' ? 'bg-blue-600 text-white' : 'hover:bg-gray-300 dark:hover:bg-gray-700'}`}>
                            PTV Ranges (Lookup)
                        </button>
                    </div>
                </div>
                <div>
                    <label className="block text-md font-medium text-gray-700 dark:text-gray-300 mb-2">Metrics to Use</label>
                    <div className="flex space-x-4">
                         <label className="flex items-center space-x-2 cursor-pointer">
                            <input type="checkbox" checked={data.useR50} onChange={e => onConfigChange('useR50', e.target.checked)} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                            <span>R50% Data</span>
                         </label>
                         <label className="flex items-center space-x-2 cursor-pointer">
                            <input type="checkbox" checked={data.useD2cm} onChange={e => onConfigChange('useD2cm', e.target.checked)} className="form-checkbox h-5 w-5 text-blue-600 rounded" />
                            <span>D2cm Data</span>
                         </label>
                    </div>
                </div>
            </div>
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-300 dark:border-gray-700">
          <h3 className="text-2xl font-bold text-blue-800 dark:text-blue-400 mb-2">PTV Data Table</h3>
          <div className="p-4 mb-4 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded-lg text-sm">
            <h4 className="font-bold mb-1">{data.dataType === 'points' ? 'How PTV Points Work:' : 'How PTV Ranges Work:'}</h4>
            {data.dataType === 'points' ?
                <p>Enter specific PTV data points. The calculator will linearly interpolate between these points.</p> :
                <p>Enter PTV ranges (e.g., Min 0, Max 10). The calculator will find which range the PTV falls into.</p>
            }
          </div>
          {/* --- MODIFICATION: Full data table JSX is now included --- */}
          <div className="overflow-x-auto">
            <table className="w-full text-left rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
              <thead className="bg-gray-200 dark:bg-gray-700">
                <tr>
                  {data.dataType === 'points' ? (
                    <th className="px-4 py-3 text-sm font-semibold">PTV Volume (cc)</th>
                  ) : (
                    <>
                      <th className="px-4 py-3 text-sm font-semibold">PTV Min (cc)</th>
                      <th className="px-4 py-3 text-sm font-semibold">PTV Max (cc)</th>
                    </>
                  )}
                  <th className="px-4 py-3 text-sm font-semibold">R50% Per Protocol</th>
                  <th className="px-4 py-3 text-sm font-semibold">R50% Acceptable</th>
                  <th className="px-4 py-3 text-sm font-semibold">D2cm[%] Per Protocol</th>
                  <th className="px-4 py-3 text-sm font-semibold">D2cm[%] Acceptable</th>
                  <th className="px-4 py-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                  {data.customTable.map((row, index) => (
                    <tr key={index}>
                      {data.dataType === 'points' ? (
                         <td className="px-2 py-2"><input type="number" value={row.ptv || ''} onChange={e => handleTableChange(index, 'ptv', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.1" /></td>
                      ) : (
                         <>
                            <td className="px-2 py-2"><input type="number" value={row.ptvMin || ''} onChange={e => handleTableChange(index, 'ptvMin', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.1" /></td>
                            <td className="px-2 py-2"><input type="number" value={row.ptvMax || ''} onChange={e => handleTableChange(index, 'ptvMax', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.1" /></td>
                         </>
                      )}
                      <td className="px-2 py-2"><input type="number" value={row.r50p || ''} onChange={e => handleTableChange(index, 'r50p', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.01" /></td>
                      <td className="px-2 py-2"><input type="number" value={row.r50v || ''} onChange={e => handleTableChange(index, 'r50v', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.01" /></td>
                      <td className="px-2 py-2"><input type="number" value={row.d2cmp || ''} onChange={e => handleTableChange(index, 'd2cmp', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.1" /></td>
                      <td className="px-2 py-2"><input type="number" value={row.d2cmv || ''} onChange={e => handleTableChange(index, 'd2cmv', e.target.value)} className="w-full px-2 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg text-sm" step="0.1" /></td>
                      <td className="px-2 py-2">
                          <button onClick={() => handleRemoveRow(index)} className="text-red-500 hover:text-red-700 transition duration-200" disabled={data.customTable.length === 1}>
                              <Trash2 size={20} />
                          </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-start mt-4">
              <button onClick={handleAddRow} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow-md hover:bg-blue-700">
                  <Plus size={20} className="mr-2" /> Add Row
              </button>
          </div>
        </div>
        
        <div className="flex justify-center mt-6">
            <button onClick={handleSave} className={`flex items-center px-6 py-3 rounded-lg font-semibold ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`} disabled={isSaving}>
                {isSaving ? 'Saving...' : <><Edit size={20} className="mr-2" /> Save My Data</>}
            </button>
        </div>
        {saveStatus === 'success' && (<div className="mt-4 p-4 rounded-lg bg-green-100 text-green-700 text-center"><p>Data saved successfully!</p></div>)}
        {saveStatus === 'error' && (<div className="mt-4 p-4 rounded-lg bg-red-100 text-red-700 text-center"><p>Failed to save data.</p></div>)}
      </div>
    );
};

export default InstitutionalData;